import type { ClaimCandidateStatus } from "@hp/types";
import { formatCount, formatINRRange, CLAIM_STATUS_LABELS } from "../../lib/format";
import type { PipelineStageSummary } from "../../api/types";

const CHART_GRID_BG =
  "bg-zinc-100 bg-[length:25%_100%] bg-[image:linear-gradient(to_right,rgba(161,161,170,0.3)_1px,transparent_1px)]";

export interface PipelineFunnelChartProps {
  stages: PipelineStageSummary[];
}

/**
 * Single-hue ordinal bars (zinc-800) on a subtle grid - stage identity
 * comes from row labels, not bar color.
 */
export function PipelineFunnelChart({ stages }: PipelineFunnelChartProps) {
  const maxValue = Math.max(1, ...stages.map((s) => s.value_high_total));

  return (
    <div className="space-y-4">
      <div className="hidden items-center gap-3 sm:flex sm:gap-4 sm:pl-20 lg:pl-24">
        <div className="relative h-3 flex-1">
          {[0, 25, 50, 75, 100].map((mark) => (
            <span
              key={mark}
              className="absolute top-0 font-mono text-[0.625rem] text-zinc-500"
              style={{ left: `${mark}%`, transform: mark === 100 ? "translateX(-100%)" : mark === 0 ? "none" : "translateX(-50%)" }}
            >
              {mark}%
            </span>
          ))}
        </div>
        <div className="w-32 shrink-0 lg:w-44" />
      </div>
      {stages.map((stage) => {
        const pct = Math.max(4, Math.round((stage.value_high_total / maxValue) * 100));
        return (
          <div key={stage.status} className="space-y-1.5 sm:space-y-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <div className="text-sm font-medium text-zinc-700 sm:w-20 sm:shrink-0 lg:w-24">
                {CLAIM_STATUS_LABELS[stage.status as ClaimCandidateStatus] ?? stage.status}
              </div>
              <div
                className={["h-6 flex-1 rounded-md", CHART_GRID_BG].join(" ")}
                role="img"
                aria-label={`${CLAIM_STATUS_LABELS[stage.status as ClaimCandidateStatus] ?? stage.status}: ${formatCount(stage.count)} claims, ${formatINRRange(stage.value_low_total, stage.value_high_total)}`}
              >
                <div
                  className="h-6 rounded-r-md bg-zinc-800"
                  style={{ width: `${pct}%` }}
                  title={`${CLAIM_STATUS_LABELS[stage.status as ClaimCandidateStatus] ?? stage.status}: ${formatCount(stage.count)} claims, ${formatINRRange(stage.value_low_total, stage.value_high_total)}`}
                />
              </div>
              <div className="text-sm text-zinc-700 sm:w-32 sm:shrink-0 sm:text-right lg:w-44">
                <span className="font-mono tabular-nums font-medium">{formatCount(stage.count)}</span>
                <span className="text-zinc-500"> claims &middot; </span>
                <span className="font-mono tabular-nums">{formatINRRange(stage.value_low_total, stage.value_high_total)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
